import type {WizardData} from '../index'

type ManualData = WizardData['manualData']

/**
 * Teachers not yet confirmed (OnboardingWizard.teachersDraft).
 *
 * Teachers reach the school only through the bulk import on Review Teachers → Continue. Until
 * then the list lives in the wizard state alone, and the wizard's localStorage copy is deleted
 * on logout, so "Save and finish later" followed by a logout lost every teacher typed, uploaded
 * or edited on Review. This draft keeps that list in preferences until the import succeeds.
 *
 * Its own key, never `setup`: node-api reads `setup` for the onboarding funnel, and a teacher
 * list there would mark assign_teachers saved before the user confirmed anything.
 */
export type OnboardingTeachersDraft = {
  teachers: ManualData['teachers']
  deletedTeachers?: ManualData['deletedTeachers']
  /** ISO time of the write; compared with `teachersConfirmedAt` on resume. */
  savedAt: string
}

export const buildTeachersDraft = (
  manualData: ManualData,
  savedAt: string
): OnboardingTeachersDraft => {
  const deletedTeachers = manualData?.deletedTeachers ?? []
  return {
    teachers: manualData?.teachers ?? [],
    ...(deletedTeachers.length > 0 ? {deletedTeachers} : {}),
    savedAt,
  }
}

const timeOf = (value: unknown): number => {
  const time = typeof value === 'string' ? Date.parse(value) : NaN
  return Number.isNaN(time) ? -Infinity : time
}

/**
 * The draft a resume should restore, or null. It wins over whatever GET onboarding/data returned
 * unless a teachers import was confirmed after it was saved (`confirmedAt`): edits made on Review
 * after an earlier Continue must not be replaced by the older server list.
 */
export const teachersDraftToRestore = (
  draft: unknown,
  confirmedAt: unknown
): OnboardingTeachersDraft | null => {
  if (!draft || typeof draft !== 'object') return null
  const candidate = draft as Partial<OnboardingTeachersDraft>
  if (!Array.isArray(candidate.teachers)) return null
  const deleted = Array.isArray(candidate.deletedTeachers) ? candidate.deletedTeachers : []
  if (candidate.teachers.length === 0 && deleted.length === 0) return null
  if (timeOf(candidate.savedAt) <= timeOf(confirmedAt)) return null
  return candidate as OnboardingTeachersDraft
}
